import { useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "../styles/reportsTabStyles.css";

import {
  getUserFailedLoginAttempts,
  getUserSuccessLoginAttempts,
  getUserActiveSessions,
  getUserLoginByIPDetail,
  getUserLoginByIPSummary,
  getUserPasswordResetActivities,
  getActiveUsers,
  getUsersPageTrackingDetails
} from "../services/adminPanel_service";

const REPORT_TYPES = {
  FAILED_LOGIN: "failedLogin",
  USER_LOGIN: "userLogin",
  ACTIVE_SESSIONS: "activeSessions",
  IP_DETAIL: "ipDetail",
  IP_SUMMARY: "ipSummary",
  PASSWORD_RESET: "passwordReset",
  USER_ACTIVITY: "userActivity"
};

const ReportsTab = () => {

  const today = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(today.getMonth() - 1);

  const [activeReport, setActiveReport] = useState(REPORT_TYPES.FAILED_LOGIN);
  const [rowData, setRowData] = useState([]);
  const [users, setUsers] = useState([]);

  const [counts, setCounts] = useState({
    admin: 0,
    contact: 0
  });

  const [filters, setFilters] = useState({
    startDate: oneMonthAgo,
    endDate: today,
    userId: 0,
    contactId: 0
  });

  const formatDateInput = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  };

  const formatDateApi = (date) => {
    const d = new Date(date);
    return d.toISOString();
  };

  const handleDateChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: new Date(value)
    }));
  };

  const handleUserChange = (value) => {
    var contactid = 0;
    var userid = 0;
    const match = users.find((item) => item.userId === value);
    if(match){
        if (match.ownerTypeId != undefined && match.userId != undefined) {
        if (match.ownerTypeId == 0)
            userid = match.userId;
        else
            contactid = match.userId;
        }
    }
    else{
        userid = value
    }
    setFilters(prev => ({
      ...prev,
      userId: userid,
      contactId:contactid
    }));
  };

  /* ---------------- GRID COLUMNS ---------------- */

  const getColumnDefs = () => {

    switch (activeReport) {

      case REPORT_TYPES.FAILED_LOGIN:
        return [
          { headerName: "Time stamp", field: "lastLogin", flex:1 },
          { headerName: "Failed User Name", field: "failedUserName", flex:1 },
          { headerName: "Contact Name", field: "contactName", flex:1 },
          { headerName: "IP Address", field: "ipAddress", flex:1 },
          { headerName: "Domain Name", field: "domainName", flex:1 }
        ];

      case REPORT_TYPES.USER_LOGIN:
        return [
          { headerName: "Name", field: "contactName", flex:1 },
          { headerName: "User ID", field: "contactId", flex:1 },
          { headerName: "Time stamp", field: "lastLogin", flex:1 },
          { headerName: "IP Address", field: "ipAddress", flex:1 },
          { headerName: "Domain Name", field: "domainName", flex:1 }
        ];

      case REPORT_TYPES.ACTIVE_SESSIONS:
        return [
          { headerName: "Name", field: "logUserName", flex:1 },
          { headerName: "User ID", field: "loginUserId", flex:1 },
          { headerName: "Time Stamp", field: "lastLogin", flex:1 },
          { headerName: "IP Address", field: "ipAddress", flex:1 },
          { headerName: "Login Type", field: "loginType", flex:1 },
          { headerName: "Domain Name", field: "domainName", flex:1 }
        ];

      case REPORT_TYPES.IP_DETAIL:
        return [
          { headerName: "IP Address", field: "loginIP", flex:1 },
          { headerName: "Name", field: "name", flex:1 },
          { headerName: "User ID", field: "contactId", flex:1 },
          { headerName: "Total Logins", field: "totalLogins", flex:1 },
          { headerName: "First Login", field: "firstLoginDate", flex:1 },
          { headerName: "Last Login", field: "lastLoginDate", flex:1 }
        ];

      case REPORT_TYPES.IP_SUMMARY:
        return [
          { headerName: "IP Address", field: "loginIP", flex:1 },
          { headerName: "Count Contacts", field: "countContacts", flex:1 },
          { headerName: "First Login", field: "firstLoginDate", flex:1 },
          { headerName: "Last Login", field: "lastLoginDate", flex:1 }
        ];

      case REPORT_TYPES.PASSWORD_RESET:
        return [
          { headerName: "Name", field: "contactName", flex:1 },
          { headerName: "User ID", field: "webUserId", flex:1 },
          { headerName: "Changed By", field: "requestedBy", flex:1 },
          { headerName: "Created Date", field: "createdDateTime", flex:1 }
        ];

      case REPORT_TYPES.USER_ACTIVITY:
        return [
          { headerName: "Name", field: "firstName", flex:1 },
          { headerName: "URL", field: "pageUrl", flex:1 },
          { headerName: "Page", field: "pageTitle", flex:1 },
          { headerName: "Accessed IP", field: "accessedIP", flex:1 },
          { headerName: "Session ID", field: "sessionId", flex:1 },
          { headerName: "Browser", field: "browserUsed", flex:1},
          { headerName: "Accessed Date", field: "accessedDateFilter", flex:1 }
        ];

      default:
        return [];
    }
  };

  const columnDefs = getColumnDefs();

  /* ---------------- DATA LOADING ---------------- */

  const loadGridData = async () => {

    const from = formatDateApi(filters.startDate);
    const to = formatDateApi(filters.endDate);

    try {

      let response;

      switch (activeReport) {

        case REPORT_TYPES.FAILED_LOGIN:
          response = await getUserFailedLoginAttempts(from, to);
          break;

        case REPORT_TYPES.USER_LOGIN:
          response = await getUserSuccessLoginAttempts(from, to);
          break;

        case REPORT_TYPES.ACTIVE_SESSIONS:
          response = await getUserActiveSessions(from, to);

          const admins = response.data.filter(x => x.loginType === "Admin").length;
          const contacts = response.data.filter(x => x.loginType === "Contact").length;

          setCounts({
            admin: admins,
            contact: contacts
          });

          break;

        case REPORT_TYPES.IP_DETAIL:
          response = await getUserLoginByIPDetail(from, to);
          break;

        case REPORT_TYPES.IP_SUMMARY:
          response = await getUserLoginByIPSummary(from, to);
          break;

        case REPORT_TYPES.PASSWORD_RESET:
          response = await getUserPasswordResetActivities(from, to);
          break;

        case REPORT_TYPES.USER_ACTIVITY:

          response = await getUsersPageTrackingDetails(
            filters.userId || 0,
            filters.contactId || 0,
            from,
            to
          );

          break;

        default:
          return;
      }

      setRowData(response.data || []);

    } catch {
      setRowData([]);
    }
  };

  /* ---------------- USERS DROPDOWN ---------------- */

  const loadUsers = async () => {
    try {
      const res = await getActiveUsers();
      setUsers(res.data || []);
    }
    catch {
      setUsers([]);
    }
  };

  /* ---------------- EFFECTS ---------------- */

  useEffect(() => {
    loadGridData();
  }, [activeReport, filters.startDate, filters.endDate, filters.userId]);

  useEffect(() => {
    loadUsers();
  }, []);

  /* ---------------- UI ---------------- */

  return (
    <div className="row">

      {/* LEFT NAVIGATION */}

      <div id="reportLeftNavigation" className="col-sm-3">

        <ul style={{ width: "auto", height: "calc(100vh + 50px)" }}>

          {Object.entries({
            FailedLoginAttempts: REPORT_TYPES.FAILED_LOGIN,
            UserLogin: REPORT_TYPES.USER_LOGIN,
            ActiveSessions: REPORT_TYPES.ACTIVE_SESSIONS,
            UserLoginByIPDetail: REPORT_TYPES.IP_DETAIL,
            UserLoginByIPSummary: REPORT_TYPES.IP_SUMMARY,
            PasswordResetActivity: REPORT_TYPES.PASSWORD_RESET,
            UserActivity: REPORT_TYPES.USER_ACTIVITY
          }).map(([label, value]) => (

            <li key={value}>
              <a
                className={activeReport === value ? "active" : ""}
                onClick={() => setActiveReport(value)}
              >
                {label.replace(/([A-Z])/g, " $1").trim()}
              </a>
            </li>

          ))}

        </ul>
      </div>

      {/* RIGHT PANEL */}

      <div className="col-sm-9 generic-kendoGrid-custom admin-kendoGrid-custom">

        {/* FILTER PANEL */}

        <div 
            style={{ 
                marginBottom: "15px",
                marginTop:'15px',
                display:'flex',
                flexDirection:'row',
            }}
        >

          <div className="col-sm-3">
            <label>Start Date</label>
            <input
              type="date"
              className="form-control"
              value={formatDateInput(filters.startDate)}
              onChange={(e) => handleDateChange("startDate", e.target.value)}
            />
          </div>

          <div className="col-sm-3">
            <label>End Date</label>
            <input
              type="date"
              className="form-control"
              value={formatDateInput(filters.endDate)}
              onChange={(e) => handleDateChange("endDate", e.target.value)}
            />
          </div>

          {/* USER DROPDOWN */}

          {activeReport === REPORT_TYPES.USER_ACTIVITY && (

            <>
              <div className="col-sm-4">
                <label>User</label>
                <select
                  className="form-control"
                  value={filters.userId}
                  onChange={(e) => handleUserChange(e.target.value)}
                >
                  <option value="">Please Select</option>

                  {users.map(u => (
                    <option key={u.userId} value={u.userId}>
                      {u.contactFullName}
                    </option>
                  ))}

                </select>
              </div>
            </>
          )}

          {activeReport === REPORT_TYPES.ACTIVE_SESSIONS && (

          <div className="col-sm-6" style={{
      alignContent: "end"
    }}>
  <div
    style={{
      display: "flex",
      flexDirection: "row",
      alignItems: "center"
    }}
  >
    <span>
      <strong>Total Count (Admin):</strong> {counts.admin}
    </span>

    &nbsp;&nbsp;

    <span>
      <strong>Total Count (Contact):</strong> {counts.contact}
    </span>
  </div>
</div>

        )}

        </div>

        {/* GRID */}

        <div
          className="ag-theme-alpine"
          style={{ height: 500, width: "100%" }}
        >
          <AgGridReact
            theme={"legacy"}
            rowData={rowData}
            columnDefs={columnDefs}
            pagination={true}
            animateRows={true}
            tooltipShowDelay={200}
            defaultColDef={{
                tooltipValueGetter: (params) => params.value
            }}
          />
        </div>

      </div>

    </div>
  );
};

export default ReportsTab;